import {Flex, Image, Text} from 'exsportia-components'
import React, {FC, useCallback, useEffect, useState} from 'react'
import {useLocation, useNavigate, useParams, useSearchParams} from 'react-router-dom'

import {useVenue} from '../api/venue/hooks'
import {getVenueLogoUrl} from '../api/venue/venue-logo'
import {NavMenu} from '../components/nav-menu/nav-menu'
import {useAuthState} from '../services/auth/hooks'
import {useAppLoading} from '../services/global/hooks'
import {useStoreCart} from '../services/store/cart'
import {ExsportiaIcon} from '../ui/components/exsportia-icon/exsportia-icon'
import {useLocale} from '../utils/i18n'
import {getItemFromLocalStorage} from '../utils/local-storage'
import {DownloadAppHeader} from './components/download-app-header'
import {ProfileMenu} from './components/profile-menu'
import {SelectLanguage} from './components/select-language-menu'
import {SignInSignUpButtons} from './components/signInSignUpButtons'
import {VenueGroupSwitcher} from './components/venue-group-switcher'

const DownloadAppHeaderToUse: FC = () => {
  const isClosed = getItemFromLocalStorage('isDownloadHeaderClosed') === 'true'
  if (isClosed) {
    return null
  }
  return <DownloadAppHeader />
}

// The cart lives in the header, next to the profile: icon + line count,
// rendered on every page whenever the cart holds anything — a shopper leaving
// the store must not lose sight of it (Dima 2026-08-19).
const HeaderCartButton: FC<{venueId?: string}> = ({venueId}) => {
  const navigate = useNavigate()
  const {count} = useStoreCart(venueId)
  if (!venueId || count === 0) {
    return null
  }
  return (
    <Flex
      p='8px 12px'
      gap='6px'
      cursor='pointer'
      alignItems='center'
      borderRadius='12px'
      backgroundColor='rgba(0, 16, 61, 0.06)'
      onClick={() => navigate(`/${venueId}/store/cart`)}
    >
      <ExsportiaIcon icon='shoppingCart' />
      <Text settings={{fontWeight: 600}}>{String(count)}</Text>
    </Flex>
  )
}

export const Header: FC = () => {
  const {venueId} = useParams()
  const {isLoggedIn, signIn, signUp} = useAuthState(venueId)
  const [registerTriggered, setRegisteredTriggered] = useState(false)
  const {isLoading} = useAppLoading()
  const {l} = useLocale()
  const[searchParams] = useSearchParams()
  const navigate = useNavigate()
  const params = useParams<{venueId: string}>()
  const {pathname} = useLocation()
  const pathArr = pathname.split('/').filter(el => el !== '')
  const {data: venueData} = useVenue()
  const handleSignIn = useCallback(() => signIn(), [signIn])
  const handleSignUp = useCallback(() => signUp(), [signUp])
  const handleNavigateToBookingType = useCallback(() => {
    if (!isLoading) {
      navigate(`/${params.venueId}`)
    }
  }, [isLoading])
  useEffect(() => {
    if (searchParams.has('register') && !registerTriggered) {
      setRegisteredTriggered(true)
      navigate('./')
      handleSignUp()
    }
  }, [])
  return (
    <>
      <DownloadAppHeaderToUse />
      <Flex
        display={(pathArr.length < 2 || !isLoggedIn) ? 'flex' : ['none', 'flex']}
        bg={'white'}
        justifyContent={'center'}
        borderRadius={['24px', 0]}
        m={['16px 16px 0 16px', 0]}
        borderBottom={'solid 1px #BFC1C755'}
      >
        <Flex
          width={['100%', '90%']}
          flexDirection={'row'}
          py={'16px'}
          px={'8px'}
          zIndex={2}
          height='max-content'
          maxHeight='64px'
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Flex
            width={['unset', '400px']}
            justifyContent='start'
          >
            <Flex
              cursor={'pointer'}
              onClick={handleNavigateToBookingType}
              alignItems={'center'}
              height={'100%'}
              width='100%'
            >
              <Flex justifyContent={'center'}>
                <Image
                  width={'48px'}
                  height={'48px'}
                  borderRadius={['16px', '12px']}
                  aspectRatio={1}
                  mr={'16px'}
                  size={'contain'}
                  src={getVenueLogoUrl(venueData)}
                />
              </Flex>
              <Text
                styleType='verticalCropped'
                settings={{
                  width: '100%',
                  fontSize: ['18px', '20px', '24px'],
                  lineHeight: ['24px', '32px'],
                  fontWeight: 600,
                  croppedLines: 2,
                }}
              >
                {venueData?.name || l('venueName')}
              </Text>
            </Flex>
            <VenueGroupSwitcher />
          </Flex>
          <NavMenu
            notForMobile={true}
            publicOnly={!isLoggedIn}
          />
          <Flex
            width={['unset', '400px']}
            justifyContent='end'
          >
            <Flex
              gap={'8px'}
              alignItems={'center'}
            >
              <HeaderCartButton venueId={venueId} />
              {!isLoggedIn && <SelectLanguage display={'flex'} />}
              {isLoggedIn ? (
                <ProfileMenu />
              ) : (
                <SignInSignUpButtons
                  display={['none', 'flex']}
                  handleSignIn={handleSignIn}
                  handleSignUp={handleSignUp}
                />
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </>
  )
}
